import type { ComputedRef, Ref } from 'vue';
import type { LocationQuery } from 'vue-router';
import type { ResultItem } from '@/utils/file';
import type {
  InitialData as DriverReportSearchFormInitialData,
  ValidData as DriverReportSearchFormValidData,
} from '@/components/searchForms/DriverReportSearchForm.vue';

import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { format } from 'date-fns-tz';
import _isEqual from 'lodash/isEqual';

import { useUserStore } from '@/stores/useUserStore';
import { fetchGetDriverReport } from '@/helpers/fetchers';
import * as routeNames from '@/constants/routeNames';
import { ReportResponse } from '@/models/reports';

export interface DriverReportViewComposable {
  search: (formData: DriverReportSearchFormValidData) => void;
  initialData: ComputedRef<DriverReportSearchFormInitialData>;
  isFetchingData: Ref<boolean>;
  total: Ref<number>;
  driverReportResults: Ref<ReportResponse[] | null>;
  resultsForCsv: Ref<ResultItem[]>;
}

export default function useDriverReportView(
  queryCurPage: Readonly<Ref<number>>,
  queryPageSize: Readonly<Ref<number>>,
  queryDate: Readonly<Ref<Date | undefined>>,
): DriverReportViewComposable {
  const router = useRouter();
  const route = useRoute();
  const userStore = useUserStore();

  const userTimeZone = computed(() => userStore.user.timezone);

  const initialData = computed<DriverReportSearchFormInitialData>(() => {
    let date;
    if (queryDate.value) {
      date = format(queryDate.value, 'yyyy-MM-dd');
    }
    return {
      date,
    };
  });

  const isFetchingData = ref(false);
  const driverReportResults = ref<ReportResponse[] | null>(null);

  const total = computed(() => {
    if (!Array.isArray(driverReportResults.value)) return 0;
    return driverReportResults.value.length;
  });

  const resultsForCsv = computed<ResultItem[]>(() => {
    if (!Array.isArray(driverReportResults.value)) return [];
    return driverReportResults.value.map((item) => {
      const { content, ...resultItem } = item;
      return resultItem;
    });
  });

  function search(formData: DriverReportSearchFormValidData): void {
    const query = { ...route.query, ...formData };
    const isReplace = _isEqual(route.query, query);
    void router.push({
      name: routeNames.DRIVER_REPORT,
      query,
      force: true,
      replace: isReplace,
    });
  }

  async function fetchData(newQuery: LocationQuery): Promise<void> {
    /* eslint-disable no-unused-vars, @typescript-eslint/no-unused-vars */
    // TODO page/page_size for endpoint
    const curPage = queryCurPage.value;
    const pageSize = queryPageSize.value;
    /* eslint-enable no-unused-vars, @typescript-eslint/no-unused-vars */
    driverReportResults.value = null;
    if (!queryDate.value) return;
    isFetchingData.value = true;
    await fetchGetDriverReport(userTimeZone.value, queryDate.value)
      .then((data) => {
        if (!_isEqual(route.query, newQuery)) {
          return;
        }
        driverReportResults.value = data;
      })
      .finally(() => {
        isFetchingData.value = false;
      });
  }

  watch(
    route,
    async (newRoute) => {
      if (newRoute.name === routeNames.DRIVER_REPORT) {
        await fetchData(newRoute.query);
      }
    },
    { immediate: true },
  );

  return {
    search,
    initialData,
    isFetchingData,
    total,
    driverReportResults,
    resultsForCsv,
  };
}
